import React, { useState, useEffect, useRef } from 'react';
import axios from '../axiosInstance';
import './CompanySelector.css';

/**
 * CompanySelector - Reusable component for selecting a company
 *
 * Features:
 * - Auto-loads all companies on mount
 * - Search/filter functionality
 * - Auto-fetches stores when company is selected
 * - Clean, self-contained styling
 * - Dropdown auto-sizes to content
 * - Supports multi-company selection with checkboxes (for "by company" mode)
 * - Company-based access control: shows all companies but disables unauthorized ones
 * - "Request Access" button for unauthorized companies
 *
 * @param {function} onCompanySelect - Callback when company is selected (company, stores)
 * @param {function} onClear - Callback when selection is cleared
 * @param {string} placeholder - Input placeholder text
 * @param {boolean} autoFetchStores - Whether to automatically fetch stores (default: true)
 * @param {boolean} multiSelect - Enable multi-select with checkboxes (default: false)
 * @param {array} selectedCompanies - Controlled array of selected company codes (for multiSelect mode)
 * @param {function} onMultiSelectChange - Callback when selected companies change in multiSelect mode (array of company codes)
 * @param {function} onSearchChange - Callback when search text changes (for real-time filtering)
 * @param {array} userCompanyAccess - Array of company codes the user has access to (for access control)
 * @param {boolean} isSuperAdmin - Whether the user is a super admin (bypasses access control)
 * @param {function} onRequestAccess - Callback when user clicks "Request Access" (companyCode, companyName)
 * @param {array} pendingRequests - Array of company codes with pending access requests
 */
const CompanySelector = ({
    onCompanySelect,
    onClear,
    placeholder = "Type company code or name...",
    autoFetchStores = true,
    multiSelect = false,
    selectedCompanies = [],
    onMultiSelectChange,
    onSearchChange,
    userCompanyAccess = [],
    isSuperAdmin = false,
    onRequestAccess,
    pendingRequests = [],
    // Optional controlled value — when provided, the picker hydrates its
    // display from this on mount and whenever it changes upstream. Used by
    // GAC to preserve the picked company across action / mode switches.
    selectedCompanyValue = null
}) => {
    const [companySearch, setCompanySearch] = useState('');
    const [selectedCompany, setSelectedCompany] = useState(selectedCompanyValue || null);
    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [allCompanies, setAllCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [loadingStores, setLoadingStores] = useState(false);
    const [containerWidth, setContainerWidth] = useState(300);

    // Refs for click-outside detection and width calculation
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    // Fetch all companies on mount
    useEffect(() => {
        fetchAllCompanies();
    }, []);

    // Sync internal display when the controlled selectedCompanyValue changes
    // (e.g., parent restores a previously-picked company after this picker
    // remounted). Compare by code to avoid loops on equivalent objects.
    useEffect(() => {
        if (multiSelect) return;
        const incomingCode = selectedCompanyValue?.companyCode || selectedCompanyValue?.code || null;
        const currentCode = selectedCompany?.companyCode || selectedCompany?.code || null;
        if (incomingCode !== currentCode) {
            setSelectedCompany(selectedCompanyValue || null);
        }
    }, [selectedCompanyValue, multiSelect]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-refresh companies every 30 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            console.log('[CompanySelector] Auto-refreshing company list (30-minute timer)...');
            fetchAllCompanies(true);
        }, 30 * 60 * 1000); // 30 minutes

        return () => clearInterval(interval);
    }, []);

    // Calculate container width based on longest company string after companies load
    useEffect(() => {
        if (allCompanies.length > 0) {
            // Use a temporary div to measure text width
            const measureDiv = document.createElement('div');
            measureDiv.style.position = 'absolute';
            measureDiv.style.visibility = 'hidden';
            measureDiv.style.whiteSpace = 'nowrap';
            measureDiv.style.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif';
            measureDiv.style.fontWeight = '700'; // Match code font weight
            measureDiv.style.padding = '12px 16px';
            document.body.appendChild(measureDiv);

            let maxWidth = 300; // Start with minimum width

            allCompanies.forEach(company => {
                // Measure the full line: "CODE - CompanyName" with proper spacing
                measureDiv.innerHTML = `<span style="font-weight: 700;">${company.companyCode}</span> - ${company.companyName}`;
                const fullWidth = measureDiv.offsetWidth + 60; // Extra padding for borders and scrollbar
                maxWidth = Math.max(maxWidth, fullWidth);
            });

            document.body.removeChild(measureDiv);

            // Cap at 600px max
            const finalWidth = Math.min(maxWidth, 600);
            setContainerWidth(finalWidth);
        }
    }, [allCompanies]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        if (showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSuggestions]);

    const fetchAllCompanies = async (forceRefresh = false) => {
        // Only skip if not forcing and already loaded
        if (!forceRefresh && (loadingCompanies || allCompanies.length > 0)) return;

        try {
            setLoadingCompanies(true);
            const { data } = await axios.get('/companies');
            const companyList = data.companyList || [];

            const companies = companyList
                .filter(company => company.code && company.name)
                .map(company => ({
                    companyCode: company.code,
                    companyName: company.name
                }))
                .sort((a, b) => a.companyCode.localeCompare(b.companyCode));

            setAllCompanies(companies);
            console.log(`[CompanySelector] Loaded ${companies.length} companies`);
        } catch (err) {
            console.error('Error fetching companies:', err);
        } finally {
            setLoadingCompanies(false);
        }
    };

    const searchCompanies = (searchText) => {
        // In multi-select mode with existing selections, require at least 2 characters to show dropdown
        // This prevents showing all companies after selecting one
        if (multiSelect && selectedCompanies.length > 0 && (!searchText || searchText.length < 2)) {
            setCompanySuggestions([]);
            setShowSuggestions(false);
            return;
        }

        // If empty or just 1 character, show all companies (for initial state)
        if (!searchText || searchText.length < 2) {
            setCompanySuggestions(allCompanies);
            setShowSuggestions(true);
            return;
        }

        // Filter companies by search text
        const searchLower = searchText.toLowerCase();
        const matches = allCompanies.filter(company =>
            company.companyCode.toLowerCase().includes(searchLower) ||
            company.companyName.toLowerCase().includes(searchLower)
        );

        setCompanySuggestions(matches);
        setShowSuggestions(matches.length > 0);
    };

    const handleInputFocus = () => {
        if (allCompanies.length > 0 && !selectedCompany) {
            // If there's a search in progress, re-run the search to show filtered results
            if (companySearch && companySearch.trim() !== '') {
                searchCompanies(companySearch);
            } else {
                // Otherwise show all companies
                setCompanySuggestions(allCompanies);
                setShowSuggestions(true);
            }
        }
    };

    const handleCompanyInputChange = (e) => {
        const value = e.target.value;
        setCompanySearch(value);
        searchCompanies(value);

        // Notify parent of search text change (for real-time filtering)
        if (onSearchChange) {
            onSearchChange(value);
        }
    };

    // Helper to check if a company is selected in multiSelect mode
    const isCompanySelected = (companyCode) => {
        if (!multiSelect) return false;
        return selectedCompanies.includes(companyCode);
    };

    // Helper to check if user has access to a company
    const hasCompanyAccess = (companyCode) => {
        if (isSuperAdmin) return true;
        if (!userCompanyAccess || userCompanyAccess.length === 0) return true; // No access control
        return userCompanyAccess.includes(companyCode);
    };

    // Helper to check if company has pending access request
    const hasPendingRequest = (companyCode) => {
        return pendingRequests.includes(companyCode);
    };

    const handleCompanySelect = async (company) => {
        // Check if user has access to this company
        if (!hasCompanyAccess(company.companyCode)) {
            // Don't allow selection of unauthorized companies
            return;
        }

        // In multiSelect mode, toggle company selection
        if (multiSelect) {
            const selected = isCompanySelected(company.companyCode);
            let newSelectedCompanies;

            if (selected) {
                // Remove company from selection
                newSelectedCompanies = selectedCompanies.filter(code => code !== company.companyCode);
            } else {
                // Add company to selection
                newSelectedCompanies = [...selectedCompanies, company.companyCode];
            }

            // Clear search after selection to reset for next search
            setCompanySearch('');

            // Notify parent of selection change
            if (onMultiSelectChange) {
                onMultiSelectChange(newSelectedCompanies);
            }

            // Clear search text in parent
            if (onSearchChange) {
                onSearchChange('');
            }

            return;
        }

        // Standard single-select mode
        setSelectedCompany(company);
        setCompanySearch('');
        setShowSuggestions(false);
        setCompanySuggestions([]);

        // Auto-fetch stores if enabled
        if (autoFetchStores) {
            try {
                setLoadingStores(true);
                const response = await axios.get(`/companies/stores?company=${company.companyCode}`);

                if (response.data && response.data.stores) {
                    // Call the parent callback with both company and stores
                    onCompanySelect && onCompanySelect(company, response.data.stores);
                } else {
                    onCompanySelect && onCompanySelect(company, []);
                }
            } catch (error) {
                console.error('Error loading stores:', error);
                onCompanySelect && onCompanySelect(company, []);
            } finally {
                setLoadingStores(false);
            }
        } else {
            // Just pass the company without stores
            onCompanySelect && onCompanySelect(company, null);
        }
    };

    const handleClear = () => {
        setSelectedCompany(null);
        setCompanySearch('');
        setShowSuggestions(false);
        setCompanySuggestions([]);

        // Notify parent of clear
        if (multiSelect && onMultiSelectChange) {
            onMultiSelectChange([]);
        }
        onClear && onClear();
    };

    return (
        <div
            className="company-selector-container"
            ref={containerRef}
        >
            <div className="company-selector-wrapper">
                {/* Show selected company with inline X */}
                {!multiSelect && selectedCompany ? (
                    <div className="company-selector-selected-display">
                        <span className="company-selector-selected-text">
                            {selectedCompany.companyName} ({selectedCompany.companyCode})
                        </span>
                        <span
                            className="company-selector-clear-x"
                            onClick={handleClear}
                            title="Clear selection"
                        >×</span>
                    </div>
                ) : (
                    <input
                        type="text"
                        placeholder={loadingStores ? "Loading stores..." : placeholder}
                        value={multiSelect ? companySearch : companySearch}
                        onChange={handleCompanyInputChange}
                        onFocus={handleInputFocus}
                        className={`company-selector-input ${showSuggestions && companySuggestions.length > 0 ? 'dropdown-open' : ''}`}
                        disabled={loadingStores || loadingCompanies}
                    />
                )}

                {showSuggestions && companySuggestions.length > 0 && (multiSelect || !selectedCompany) && (
                    <div className="company-selector-dropdown" ref={dropdownRef}>
                        {(() => {
                            // Sort companies: selected ones first, then alphabetically
                            const sortedSuggestions = [...companySuggestions].sort((a, b) => {
                                const aSelected = isCompanySelected(a.companyCode);
                                const bSelected = isCompanySelected(b.companyCode);

                                // Selected companies come first
                                if (aSelected && !bSelected) return -1;
                                if (!aSelected && bSelected) return 1;

                                // Within each group, sort alphabetically
                                return a.companyCode.localeCompare(b.companyCode);
                            });

                            return sortedSuggestions.map((company, index) => {
                                const isSelected = isCompanySelected(company.companyCode);
                                const isFirstUnselected = !isSelected && index > 0 && isCompanySelected(sortedSuggestions[index - 1].companyCode);
                                const hasAccess = hasCompanyAccess(company.companyCode);
                                const isPending = hasPendingRequest(company.companyCode);

                                return (
                                    <React.Fragment key={company.companyCode}>
                                        {/* Add separator between selected and unselected */}
                                        {isFirstUnselected && (
                                            <div style={{
                                                borderTop: '2px solid #cbd5e1',
                                                margin: '4px 0'
                                            }} />
                                        )}
                                        <div
                                            className="company-selector-item"
                                            onClick={() => handleCompanySelect(company)}
                                            style={{
                                                cursor: hasAccess ? 'pointer' : 'not-allowed',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                backgroundColor: isSelected ? '#e0e7ff' : (hasAccess ? 'transparent' : '#f3f4f6'),
                                                fontWeight: isSelected ? 600 : 400,
                                                opacity: hasAccess ? 1 : 0.6,
                                                position: 'relative',
                                                paddingRight: !hasAccess ? '120px' : '16px'
                                            }}
                                        >
                                            {multiSelect && hasAccess && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            )}
                                            {multiSelect && !hasAccess && (
                                                <input
                                                    type="checkbox"
                                                    checked={false}
                                                    disabled={true}
                                                    style={{ cursor: 'not-allowed' }}
                                                />
                                            )}
                                            <span className="company-selector-item-code" style={{ color: hasAccess ? 'inherit' : '#9ca3af' }}>
                                                {company.companyCode}
                                            </span>
                                            <span className="company-selector-item-separator" style={{ color: hasAccess ? 'inherit' : '#9ca3af' }}> - </span>
                                            <span className="company-selector-item-name" style={{ color: hasAccess ? 'inherit' : '#9ca3af' }}>
                                                {company.companyName}
                                            </span>
                                            {!hasAccess && onRequestAccess && (
                                                <button
                                                    className="company-selector-request-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRequestAccess(company.companyCode, company.companyName);
                                                    }}
                                                    disabled={isPending}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '8px',
                                                        padding: '4px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        backgroundColor: isPending ? '#d1d5db' : '#3b82f6',
                                                        color: 'white',
                                                        cursor: isPending ? 'not-allowed' : 'pointer',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={isPending ? 'Request pending' : 'Request access to this company'}
                                                >
                                                    {isPending ? 'Pending' : 'Request Access'}
                                                </button>
                                            )}
                                        </div>
                                    </React.Fragment>
                                );
                            });
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanySelector;
